import React from "react";
import { ArrowBack, DataObject, Delete, Send } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { SiteApiItem } from "../../../site-listings/services/siteListingConfig";
import { RegistryPage } from "../../registry/sitePageRegistry";
import { SEOConfigApiItem } from "../../services/seoConfig";
import { pageLabel, SEOForm } from "../../types";
import { useSEOAnalysis } from "../../hooks/useSEOAnalysis";
import BasicMetaTagsCard from "./BasicMetaTagsCard";
import OpenGraphCard from "./OpenGraphCard";
import TwitterCardCard from "./TwitterCardCard";
import SchemaMarkupCard from "./SchemaMarkupCard";
import SEOScoreGauge from "./SEOScoreGauge";
import SEOChecklistCard from "./SEOChecklistCard";
import SEOSuggestionsCard from "./SEOSuggestionsCard";
import SearchPreviewCard from "./SearchPreviewCard";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../../constants/permissions";

interface PageEditorProps {
  selectedSite: SiteApiItem | null;
  selectedConfig: SEOConfigApiItem | undefined;
  pageDefinitions: RegistryPage[];
  form: SEOForm;
  saving: boolean;
  deleting: boolean;
  onChange: <K extends keyof SEOForm>(field: K, value: SEOForm[K]) => void;
  onBack: () => void;
  onSave: (publish: boolean) => void;
  onDelete: () => void;
}

const PageEditor: React.FC<PageEditorProps> = ({
  selectedSite,
  selectedConfig,
  pageDefinitions,
  form,
  saving,
  deleting,
  onChange,
  onBack,
  onSave,
  onDelete,
}) => {
  const { hasPermission } = usePermissions();
  const canSave = hasPermission(
    selectedConfig ? PERMISSIONS.CONFIGURATION_CHANGE : PERMISSIONS.CONFIGURATION_ADD,
  );
  const canDelete = hasPermission(PERMISSIONS.CONFIGURATION_DELETE);
  const analysis = useSEOAnalysis(form);
  const pageDefinition = pageDefinitions.find((page) => page.key === form.pageName);
  const domain = selectedSite?.domain
    ? `https://${selectedSite.domain}`
    : "https://example.com";
  const path =
    pageDefinition?.path ||
    `/${form.pageName || ""}`.replace(/\/+/g, "/");
  const previewUrl = `${domain}${path}`;
  const displayLabel =
    pageDefinition?.label ||
    (selectedConfig ? pageLabel(selectedConfig.page_name) : "Add page SEO");

  return (
    <Box sx={{ pb: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Pages
      </Button>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h4" fontWeight={800}>
              {displayLabel}
            </Typography>
            {pageDefinition?.isDynamic && (
              <Chip
                size="small"
                icon={<DataObject fontSize="small" />}
                label="Dynamic page"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography color="text.secondary">{path}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {selectedConfig && canDelete && (
            <Button
              color="error"
              variant="outlined"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Delete />
                )
              }
              onClick={onDelete}
              disabled={deleting || saving}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          )}
          {canSave && (
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Send />
                )
              }
              onClick={() => onSave(true)}
              disabled={saving || deleting}
            >
              {selectedConfig?.is_active ? "Update" : "Publish"}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            <BasicMetaTagsCard
              form={form}
              onChange={onChange}
              pageDefinition={pageDefinition}
              pageNameLocked={Boolean(pageDefinition)}
            />
            <OpenGraphCard
              form={form}
              onChange={onChange}
              pageDefinition={pageDefinition}
            />
            <TwitterCardCard
              form={form}
              onChange={onChange}
              pageDefinition={pageDefinition}
            />
            <SchemaMarkupCard form={form} onChange={onChange} />
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <SearchPreviewCard
              form={form}
              previewUrl={previewUrl}
              variables={pageDefinition?.variables}
            />
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <Typography fontWeight={800} mb={2}>
                SEO Score Analysis
              </Typography>
              <SEOScoreGauge
                score={analysis.score}
                scoreLabel={analysis.scoreLabel}
                scoreColor={analysis.scoreColor}
              />
            </Box>
            <SEOChecklistCard checklist={analysis.checklist} />
            <SEOSuggestionsCard suggestions={analysis.suggestions} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PageEditor;
